import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase"; // Firebase auth and Firestore
import { doc, getDoc, setDoc } from "firebase/firestore"; // Firestore functions
import Header from "../components/Header";
import Footer from "../components/Footer";

// Utility function to calculate change in weight or sleep
const calculateChange = (dataArray, key) => {
  if (dataArray.length < 2) return "No change";

  const previousData = dataArray[dataArray.length - 2];
  const latestData = dataArray[dataArray.length - 1];

  if (parseFloat(latestData[key]) > parseFloat(previousData[key])) {
    return `${key.charAt(0).toUpperCase() + key.slice(1)} increased`;
  } else if (parseFloat(latestData[key]) < parseFloat(previousData[key])) {
    return `${key.charAt(0).toUpperCase() + key.slice(1)} decreased`;
  }

  return `${key.charAt(0).toUpperCase() + key.slice(1)} remained the same`;
};

const DataInputPage = () => {
  const [weight, setWeight] = useState("");
  const [hoursSlept, setHoursSlept] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  // Handle form submission to save or update data in Firebase
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!weight || !hoursSlept || !date) {
      setErrorMessage("All fields are required.");
      return;
    }

    setLoading(true);
    const user = auth.currentUser;
    if (user) {
      try {
        const userRef = doc(db, "healthData", user.uid);

        // Fetch existing data to calculate the insights
        const docSnap = await getDoc(userRef);
        let updatedData = { weightTrends: [], sleepPatterns: [], insights: {} };

        if (docSnap.exists()) {
          const existingData = docSnap.data();
          updatedData = { ...existingData };

          // Check if the date already exists
          const existingWeightData = updatedData.weightTrends.find(
            (entry) => entry.date === date
          );
          const existingSleepData = updatedData.sleepPatterns.find(
            (entry) => entry.date === date
          );

          if (existingWeightData) {
            // If the date exists, update the existing entry
            existingWeightData.weight = weight;
          } else {
            // Otherwise, add a new entry
            updatedData.weightTrends.push({ date, weight });
          }

          if (existingSleepData) {
            // If the date exists, update the existing entry
            existingSleepData.hours = hoursSlept;
          } else {
            // Otherwise, add a new entry
            updatedData.sleepPatterns.push({ date, hours: hoursSlept });
          }

          // Calculate average weight and sleep hours
          const totalWeight = updatedData.weightTrends.reduce((sum, entry) => sum + parseFloat(entry.weight), 0);
          const totalSleep = updatedData.sleepPatterns.reduce((sum, entry) => sum + parseFloat(entry.hours), 0);
          
          const averageWeight = totalWeight / updatedData.weightTrends.length;
          const averageSleep = totalSleep / updatedData.sleepPatterns.length;

          // Calculate the change in weight and sleep hours
          const weightChange = calculateChange(updatedData.weightTrends, 'weight');
          const sleepChange = calculateChange(updatedData.sleepPatterns, 'hours');

          // Save insights in the data
          updatedData.insights = {
            averageWeight,
            averageSleep,
            weightChange,
            sleepChange,
          };
        } else {
          // If no existing data, initialize new data structure with insights
          updatedData = {
            weightTrends: [{ date, weight }],
            sleepPatterns: [{ date, hours: hoursSlept }],
            insights: {
              averageWeight: weight,
              averageSleep: hoursSlept,
              weightChange: "No change",
              sleepChange: "No change",
            },
          };
        }

        // Save or update the data in Firestore
        await setDoc(userRef, updatedData, { merge: true });
        setSuccessMessage("Data successfully updated!");
        setTimeout(() => {
          navigate("/dashboard"); // Redirect to Dashboard after success
        }, 2000);
      } catch (error) {
        console.error("Error updating data in Firestore:", error);
        setErrorMessage("An error occurred. Please try again.");
      }
    } else {
      setErrorMessage("You must be logged in to submit data.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-500 to-blue-700 text-white">
      <Header />

      <main className="flex-grow flex flex-col p-6 lg:p-12 gap-8">
        <h2 className="text-3xl font-bold text-center">Input New Data</h2>

        {/* Data Input Form */}
        <section className="bg-white text-gray-800 rounded-lg shadow-lg p-6 max-w-3xl mx-auto">
          <h3 className="text-2xl font-semibold text-blue-600 mb-4">
            Enter Your Health Data
          </h3>

          {/* Display Success or Error Messages */}
          {successMessage && (
            <div className="bg-green-500 text-white p-3 rounded-lg mb-4">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="bg-red-500 text-white p-3 rounded-lg mb-4">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="date" className="block text-lg mb-2">
                Date
              </label>
              <input
                  type="date"
                  id="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                  max={new Date().toISOString().split("T")[0]} // Set the max date to today
                />
            </div>

            <div>
              <label htmlFor="weight" className="block text-lg mb-2">
                Weight (kg)
              </label>
              <input
                type="number"
                id="weight"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="Enter your weight"
                required
              />
            </div>

            <div>
              <label htmlFor="hoursSlept" className="block text-lg mb-2">
                Hours of Sleep
              </label>
              <input
                type="number"
                id="hoursSlept"
                value={hoursSlept}
                onChange={(e) => setHoursSlept(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="Enter hours of sleep"
                required
              />
            </div>

            <div className="flex justify-center gap-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save Data"}
              </button>
            </div>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DataInputPage;
